import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import authStorage from '../lib/authStorage';
import apiClient from '../lib/apiClient';

const AuthContext = createContext({
  user: null,
  company: null,
  isAuthenticated: false,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refreshProfile: async () => {},
});

const resolveCompanyCandidate = (profile, override) => {
  if (override && typeof override === 'object') {
    return override;
  }

  if (profile && typeof profile === 'object') {
    if (profile.company && typeof profile.company === 'object') {
      return profile.company;
    }
    if (profile.business && typeof profile.business === 'object') {
      return profile.business;
    }
    if (profile.businessDetails && typeof profile.businessDetails === 'object') {
      return profile.businessDetails;
    }
  }

  return null;
};

const normalizeUserProfile = (profile, companyOverride = null) => {
  if (!profile || typeof profile !== 'object') {
    return profile ?? null;
  }

  const companyCandidate = resolveCompanyCandidate(profile, companyOverride);

  if (!companyCandidate || typeof companyCandidate !== 'object') {
    return profile;
  }

  const normalizedCompany = {
    ...companyCandidate,
    id:
      typeof companyCandidate.id === 'string' && companyCandidate.id.trim().length > 0
        ? companyCandidate.id.trim()
        : companyCandidate.id ?? null,
    name:
      typeof companyCandidate.name === 'string'
        ? companyCandidate.name.trim() || companyCandidate.name
        : companyCandidate.name ?? null,
    logoUrl:
      typeof companyCandidate.logoUrl === 'string'
        ? companyCandidate.logoUrl.trim() || null
        : companyCandidate.logoUrl ?? null,
    locationIds: Array.isArray(companyCandidate.locationIds)
      ? companyCandidate.locationIds
          .map((entry) => (typeof entry === 'string' ? entry.trim() : null))
          .filter(Boolean)
      : [],
  };

  return {
    ...profile,
    company: normalizedCompany,
    business: normalizedCompany,
    businessDetails: normalizedCompany,
  };
};

const fetchCurrentUser = async (companyOverride = null) => {
  const response = await apiClient.get('/auth/me');
  return normalizeUserProfile(response.data, companyOverride);
};

const attemptTokenRefresh = async () => {
  try {
    await apiClient.get('/auth/me');
    return true;
  } catch (error) {
    if (error?.response?.status === 401) {
      authStorage.clearTokens();
    }
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => normalizeUserProfile(authStorage.getUser()));
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    const hasToken = !!authStorage.getAccessToken();
    if (!hasToken) {
      const rememberToken = authStorage.getRememberMeToken();
      const rememberExpiry = authStorage.getRememberMeExpiry();

      if (rememberToken && rememberExpiry && rememberExpiry > Date.now()) {
        try {
          const response = await apiClient.post('/auth/remember-me', {
            token: rememberToken,
          });

          const {
            accessToken,
            refreshToken,
            expiresIn,
            rememberMeToken,
            rememberMeExpiresAt,
          } = response.data ?? {};

          if (!accessToken || !refreshToken) {
            throw new Error('Missing tokens in remember me response');
          }

          authStorage.saveTokens({ accessToken, refreshToken, expiresIn });

          if (rememberMeToken && rememberMeExpiresAt) {
            authStorage.saveRememberMe({
              token: rememberMeToken,
              expiresAt: rememberMeExpiresAt,
            });
          } else {
            authStorage.clearRememberMe();
          }

          const profile = await fetchCurrentUser(response.data?.company);
          setUser(profile);
          authStorage.saveUser(profile);
          setLoading(false);
          return;
        } catch (error) {
          if (error?.response?.status === 401) {
            authStorage.clearRememberMe();
          }
        }
      } else if (
        rememberToken &&
        (!rememberExpiry || rememberExpiry <= Date.now())
      ) {
        authStorage.clearRememberMe();
      }

      setUser(null);
      setLoading(false);
      return;
    }

    if (authStorage.isAccessTokenExpired()) {
      const refreshed = await attemptTokenRefresh();
      if (!refreshed) {
        setUser(null);
        setLoading(false);
        return;
      }
    }

    try {
      const profile = await fetchCurrentUser();
      setUser(profile);
      authStorage.saveUser(profile);
    } catch (error) {
      authStorage.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/login', credentials);
      const {
        accessToken,
        refreshToken,
        expiresIn,
        rememberMeToken,
        rememberMeExpiresAt,
      } = response.data ?? {};
      if (!accessToken || !refreshToken) {
        throw new Error('Missing tokens in login response');
      }

      authStorage.saveTokens({ accessToken, refreshToken, expiresIn });

      if (rememberMeToken && rememberMeExpiresAt) {
        authStorage.saveRememberMe({
          token: rememberMeToken,
          expiresAt: rememberMeExpiresAt,
        });
      } else {
        authStorage.clearRememberMe();
      }

      const profile = await fetchCurrentUser(response.data?.company);
      setUser(profile);
      authStorage.saveUser(profile);
      return profile;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!authStorage.getAccessToken()) {
      setUser(null);
      authStorage.clearTokens();
      return null;
    }

    try {
      const profile = await fetchCurrentUser();
      setUser(profile);
      authStorage.saveUser(profile);
      return profile;
    } catch (error) {
      authStorage.clearTokens();
      setUser(null);
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (authStorage.getAccessToken()) {
        await apiClient.post('/auth/logout');
      }
    } catch {
      // Ignore logout failures; still clear local state.
    } finally {
      authStorage.clear();
      setUser(null);
    }
  }, []);

  const company = useMemo(() => {
    if (!user || typeof user !== 'object') {
      return null;
    }

    if (user.company && typeof user.company === 'object') {
      return user.company;
    }

    if (user.business && typeof user.business === 'object') {
      return user.business;
    }

    if (user.businessDetails && typeof user.businessDetails === 'object') {
      return user.businessDetails;
    }

    return null;
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      company,
      isAuthenticated: !!user,
      loading,
      login,
      logout,
      refreshProfile,
    }),
    [company, loading, login, logout, refreshProfile, user],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
